import { DEFAULT_WIDGET_COLOR } from "@/constants/branding";
import { ClientPortalBannerPhase, Operator } from "@/redux/slices/operator/types";
import { GetOperatorsRawResponse, GetOperatorRawResponse, GetOperatorsResponse, GetOperatorResponse } from "./types";

/**
 * The backend already normalises this, so anything unexpected here means an older backend or a
 * response we don't recognise — either way, no banner rather than a guess.
 */
const toBannerPhase = (value?: string): ClientPortalBannerPhase =>
  value === "announcement" || value === "live" ? value : "";

export const transformOperators = (res: GetOperatorsRawResponse) => {
  const operators = res._embedded.providers.map((p) => {
    const {
      provider_uuid,
      provider_key,
      domain_driver,
      domain,
      country,
      currency,
      bookable_country_list,
      available_account_types_on_register,
      email,
      joined,
      images,
      name,
      phone,
      primary,
      status,
      urls: { brochure, contact_us, privacy_policy, terms },
      permissions: { roles, permissions },
      flags,
      branding: { primary_color, secondary_color, tertiary_color, widget_color },
    } = p;
    const { logo, emblem, qr, qr_transparent } = images;

    return {
      id: provider_uuid,
      providerCode: provider_key.toLowerCase(),
      singleTenant: provider_key.toLowerCase() !== "rideminder",
      domain: domain.toLowerCase(),
      domainDriver: domain_driver.toLowerCase(),
      name,
      email,
      ownerEmail: p.owner_email,
      joined,
      images: {
        logo,
        emblem,
        qr,
        qrTransparent: qr_transparent,
      },
      phone,
      primary,
      status,
      urls: {
        brochure,
        privacy: privacy_policy,
        terms,
        contact: contact_us,
      },
      country,
      currency,
      bookableCountryList: bookable_country_list,
      availableAccountTypesOnRegister: available_account_types_on_register,
      permissions,
      roles,
      isFaStaff: p.is_fa_staff,
      branding: {
        primary: primary_color,
        dark: secondary_color,
        light: tertiary_color,
        widget: widget_color ?? DEFAULT_WIDGET_COLOR,
      },
      registerLinks: {
        driver: p.deep_links?.driver_registration ?? "",
        client: p.deep_links?.client_registration ?? "",
      },
      kindeIssuerUrl: p.kinde_issuer_url,
      kindeClientId: p.kinde_web_client_id ?? undefined,
      flags: {
        paymentInvoiceConfigured: Boolean(flags.payment_invoice_configured),
        dashboardNotesEnabled: Boolean(flags.dashboard_notes_enabled),
        useOptimizedLayout: Boolean(flags.optimized_layout),
        driverPaidByPlatform: Boolean(flags.driver_paid_by_platform),
        paymentConfigured: Boolean(flags.payment_configured),
        isSuspended: Boolean(flags.is_suspended),
        isSimpleSignupEnabled: Boolean(flags.is_simple_signup_enabled),
      },
      addOns: {
        flightTracking: Boolean(p.add_ons?.flight_tracking),
        preferredDriver: Boolean(p.add_ons?.preferred_driver),
      },
      announcement: {
        clientPortalPhase: toBannerPhase(p.announcement?.client_portal_phase),
        // Trimmed here so the banner only has to test for empty, not for whitespace a config editor left behind.
        clientPortalUrl: (p.announcement?.client_portal_url ?? "").trim(),
      },
    } as Operator;
  });

  const response: GetOperatorsResponse = {
    data: {
      operators,
    },
    links: res._links,
  };

  return response;
};

export const transformOperator = (res: GetOperatorRawResponse) => {
  const {
    provider_uuid,
    provider_key,
    domain_driver,
    domain,
    country,
    currency,
    bookable_country_list,
    available_account_types_on_register,
    email,
    joined,
    images,
    name,
    phone,
    primary,
    urls: { brochure, contact_us, privacy_policy, terms },
    permissions: { roles, permissions },
    flags,
    branding: { primary_color, secondary_color, tertiary_color, widget_color },
  } = res;
  const { logo, emblem, qr, qr_transparent } = images;

  const response: GetOperatorResponse = {
    data: {
      id: provider_uuid,
      providerCode: provider_key.toLowerCase(),
      singleTenant: provider_key.toLowerCase() !== "rideminder",
      domain: domain.toLowerCase(),
      domainDriver: domain_driver.toLowerCase(),
      name,
      email,
      ownerEmail: res.owner_email,
      joined,
      images: {
        logo,
        emblem,
        qr,
        qrTransparent: qr_transparent,
      },
      phone,
      primary,
      urls: {
        brochure,
        privacy: privacy_policy,
        terms,
        contact: contact_us,
      },
      country,
      currency,
      bookableCountryList: bookable_country_list,
      availableAccountTypesOnRegister: available_account_types_on_register,
      permissions,
      roles,
      isFaStaff: res.is_fa_staff,
      branding: {
        primary: primary_color,
        dark: secondary_color,
        light: tertiary_color,
        widget: widget_color ?? DEFAULT_WIDGET_COLOR,
      },
      registerLinks: {
        driver: res.deep_links?.driver_registration ?? "",
        client: res.deep_links?.client_registration ?? "",
      },
      kindeIssuerUrl: res.kinde_issuer_url,
      kindeClientId: res.kinde_web_client_id ?? undefined,
      flags: {
        paymentInvoiceConfigured: Boolean(flags.payment_invoice_configured),
        dashboardNotesEnabled: Boolean(flags.dashboard_notes_enabled),
        useOptimizedLayout: Boolean(flags.optimized_layout),
        driverPaidByPlatform: Boolean(flags.driver_paid_by_platform),
        paymentConfigured: Boolean(flags.payment_configured),
        isSuspended: Boolean(flags.is_suspended),
        isSimpleSignupEnabled: Boolean(flags.is_simple_signup_enabled),
      },
      addOns: {
        flightTracking: Boolean(res.add_ons?.flight_tracking),
        preferredDriver: Boolean(res.add_ons?.preferred_driver),
      },
      announcement: {
        clientPortalPhase: toBannerPhase(res.announcement?.client_portal_phase),
        // Trimmed here so the banner only has to test for empty, not for whitespace a config editor left behind.
        clientPortalUrl: (res.announcement?.client_portal_url ?? "").trim(),
      },
    },
  };

  return response;
};

/**
 * Pick the client's currently-active operator from their providers list (the account-management
 * providers history). Prefer the primary active relationship, then any active one, then the most
 * recent. This is the branding source of truth — `profile.accountMetadata.providerCode` can lag
 * behind after a detach/re-join and still point at a left operator.
 */
export const selectActiveOperator = (operators: Operator[]): Operator | undefined =>
  operators.find((operator) => operator.primary && operator.status === "active") ??
  operators.find((operator) => operator.status === "active") ??
  operators[0];
